import styled from 'styled-components';
import { SystemIconsSuccess } from '@elfsight/icons';
import { Show } from '@elfsight/lib-utils';
import {
  Button,
  getTokenFontStyle,
  Icon,
  Typography
} from '@elfsight/widget-ui';
import { createReadableClass, getInvertedColor } from '@elfsight/widget-utils';
import type { SuccessMessageProps } from '../../../common';
import { CheckoutItem } from '../blocks';
import { useCheckoutColors, useCheckoutNavigation } from '../providers';
import { useCheckoutNarrowness } from '../utils';

const COMPONENT_NAME = 'success-message';

export function SuccessMessage({
  heading,
  description,
  buttonText,
  buttonFont = getTokenFontStyle('text15Bold')
}: SuccessMessageProps) {
  const { closeCheckoutComponent } = useCheckoutNavigation();
  const { successColor, textColor, accentColor } = useCheckoutColors();
  const isNarrow = useCheckoutNarrowness();
  const invertedButtonColor = getInvertedColor(accentColor!);

  return (
    <Component>
      <Content>
        <Icon component={SystemIconsSuccess} size={44} fill={successColor} />

        <TextContent>
          <Typography
            textToken="text24Bold"
            color={successColor}
            className={createReadableClass({
              component: COMPONENT_NAME,
              element: 'heading'
            })}
          >
            {heading}
          </Typography>

          <Typography
            textToken="text16"
            className={createReadableClass({
              component: COMPONENT_NAME,
              element: 'description'
            })}
            color={textColor}
            dangerouslySetInnerHTML={{ __html: description }}
          />
        </TextContent>
      </Content>

      <Show when={isNarrow}>
        <CheckoutItem />
      </Show>

      <StyledButton
        color={accentColor}
        fontColor={invertedButtonColor}
        font={buttonFont}
        borderWidth={0}
        borderRadius={8}
        verticalPadding={10}
        horizontalPadding={22}
        onClick={closeCheckoutComponent}
      >
        {buttonText}
      </StyledButton>
    </Component>
  );
}

const Component = styled.div.attrs({
  className: createReadableClass({
    component: COMPONENT_NAME
  })
})`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 40px;

  flex: 1;
  width: 100%;
  height: 100%;
`;

const Content = styled.div.attrs({
  className: createReadableClass({
    component: COMPONENT_NAME,
    element: 'content'
  })
})`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;

  width: 100%;
`;

const TextContent = styled.div.attrs({
  className: createReadableClass({
    component: COMPONENT_NAME,
    element: 'text'
  })
})`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;

  width: 100%;

  text-align: center;
  white-space: pre-wrap;
`;

const StyledButton = styled(Button).attrs({
  className: createReadableClass({
    component: COMPONENT_NAME,
    element: 'button'
  })
})`
  min-width: 140px;
  flex-shrink: 0;
`;
